import { AfterViewInit, ChangeDetectorRef, Component, Inject, Input, NgZone, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
// amCharts imports
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import * as am5timeline from '@amcharts/amcharts5/timeline';
import { BaseChartService } from '../../../../shared/services/base-chart.service';
import { CommonService } from '../../../../shared/services/common.service';

// Timeline data interface
interface TimelineData {
  category: string;
  start: number;
  end: number;
  settings: { fill: am5.Color };
  icon?: string;
  text: string;
  startFormatted?: string;
  endFormatted?: string;
}

interface ReportData {
  reportData: any[];
  allColumnsData: any;
  reportFormData: any;
}

/**
 * Linear Timeline Graph Component
 * @author: PRachethan | 02-08-2025 
 */
@Component({
  selector: 'bi-linear-timeline-graph',
  standalone: false,
  templateUrl: './linear-timeline-graph.component.html',
  styleUrl: './linear-timeline-graph.component.scss'
})
export class LinearTimelineGraphComponent implements OnInit, AfterViewInit, OnDestroy {
  private root!: am5.Root;
  data: TimelineData[] = [];
  private linearTimelineGraphReportSubscription: any;
  private isLinearTimelineViewInitialized = false;
  private isCreatingChart = false;
  
  @Input() reportData: ReportData | null = null;

  constructor(@Inject(PLATFORM_ID) private platformId: object,
    private zone: NgZone,
    private _baseChartService: BaseChartService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _commonService: CommonService) { }

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      this.linearTimelineGraphReportSubscription = this._commonService.graphReportState.subscribe((data: ReportData) => {
        if (data && data.reportData && Array.isArray(data.reportData) && data.reportData.length > 0) {
          this.reportData = data;
          console.log("Linear Timeline Graph Data", this.reportData);
          this.data = this.transformToTimelineData(data);
          if (this.isLinearTimelineViewInitialized) {
            this.updateChart(this.data);
          }
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.isLinearTimelineViewInitialized = true;
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0) {
      this.zone.runOutsideAngular(() => {
        // Check if DOM element exists before creating chart
        const chartContainer = document.getElementById('linear-timeline-chartdiv');
        if (chartContainer) {
          this.data = this.transformToTimelineData(this.reportData!);
          this.updateChart(this.data);
        } else {
          console.log('Linear Timeline Chart - DOM element not ready in ngAfterViewInit, will retry later');
          // Retry after a short delay
          setTimeout(() => {
            const retryContainer = document.getElementById('linear-timeline-chartdiv');
            if (retryContainer) {
              this.data = this.transformToTimelineData(this.reportData!);
              this.updateChart(this.data);
            }
          }, 200);
        }
      });
    }
  }

  ngOnChanges(): void {
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0 && this.isLinearTimelineViewInitialized) {
      this.zone.runOutsideAngular(() => {
        // Check if DOM element exists before creating chart
        const chartContainer = document.getElementById('linear-timeline-chartdiv');
        if (chartContainer) {
          this.data = this.transformToTimelineData(this.reportData!);
          this.updateChart(this.data);
        } else {
          console.log('Linear Timeline Chart - DOM element not ready in ngOnChanges, will retry later');
          // Retry after a short delay
          setTimeout(() => {
            const retryContainer = document.getElementById('linear-timeline-chartdiv');
            if (retryContainer) {
              this.data = this.transformToTimelineData(this.reportData!);
              this.updateChart(this.data);
            }
          }, 200);
        }
      });
    }
  }

  private transformToTimelineData(reportData: ReportData): TimelineData[] {
    // First transform the original API data using the base chart service
    const transformedData = this._baseChartService.transformReportData(reportData);
    
    if (!transformedData || transformedData.length === 0) {
      console.warn('No transformed data available for timeline');
      return this.createSampleTimelineData();
    }

    console.log('Transformed data for timeline:', transformedData);

    // Create timeline data from the transformed data
    const timelineData: TimelineData[] = [];
    
    // Create a simple color palette
    const colors = [
      am5.color(0x67B7DC), // blue
      am5.color(0x6794DC), // darker blue
      am5.color(0x67DC75), // green
      am5.color(0xDC7567), // red
      am5.color(0xDCB767), // yellow
      am5.color(0xDC67CE), // purple
      am5.color(0x67CEDC), // cyan
      am5.color(0xCE67DC), // magenta
    ];

    // Get column names from the first transformed item
    const firstItem = transformedData[0];
    if (!firstItem) {
      return this.createSampleTimelineData();
    }

    const columnNames = Object.keys(firstItem);
    console.log('Available columns for timeline:', columnNames);

    // Try to identify date/time columns and category columns
    const dateColumns = columnNames.filter(col => 
      col.toLowerCase().includes('date') || 
      col.toLowerCase().includes('time') || 
      col.toLowerCase().includes('start') || 
      col.toLowerCase().includes('end')
    );

    const categoryColumns = columnNames.filter(col => 
      col.toLowerCase().includes('category') || 
      col.toLowerCase().includes('type') || 
      col.toLowerCase().includes('group') ||
      col.toLowerCase().includes('name')
    );

    console.log('Date columns found:', dateColumns);
    console.log('Category columns found:', categoryColumns);

    // Process each transformed item
    transformedData.forEach((item, index) => {
      let startDate: number;
      let endDate: number;
      let category: string;
      let text: string;

      // Determine start and end dates
      if (dateColumns.length >= 2) {
        // Use first two date columns as start and end
        const startCol = dateColumns[0];
        const endCol = dateColumns[1];
        startDate = this.parseDate(item[startCol]);
        endDate = this.parseDate(item[endCol]);
      } else if (dateColumns.length === 1) {
        // Use single date column and add 1 hour
        const dateCol = dateColumns[0];
        startDate = this.parseDate(item[dateCol]);
        endDate = startDate + (60 * 60 * 1000); // 1 hour later
      } else {
        // No date columns found, use current time with offset
        const now = new Date();
        startDate = now.getTime() + (index * 60 * 60 * 1000); // Each item 1 hour apart
        endDate = startDate + (60 * 60 * 1000); // 1 hour duration
      }

      // Determine category
      if (categoryColumns.length > 0) {
        category = String(item[categoryColumns[0]] || `category_${index}`);
      } else {
        category = `category_${index}`;
      }

      // Determine text (use first available string column or category)
      const textColumns = columnNames.filter(col => 
        typeof item[col] === 'string' && 
        !dateColumns.includes(col) && 
        !categoryColumns.includes(col)
      );

      if (textColumns.length > 0) {
        text = String(item[textColumns[0]] || category);
      } else {
        text = category;
      }

      timelineData.push({
        category,
        start: startDate,
        end: endDate,
        settings: { fill: colors[index % colors.length] },
        icon: '', // No icon for now, can be enhanced later
        text,
        startFormatted: new Date(startDate).toLocaleString(),
        endFormatted: new Date(endDate).toLocaleString()
      });
    });

    return timelineData.sort((a, b) => {
      return (a.category < b.category) ? -1 : (a.category > b.category) ? 1 : 0;
    });
  }

  private parseDate(dateValue: any): number {
    if (!dateValue) {
      return new Date().getTime();
    }

    if (typeof dateValue === 'number') {
      return dateValue;
    }

    if (typeof dateValue === 'string') {
      const parsed = new Date(dateValue);
      return isNaN(parsed.getTime()) ? new Date().getTime() : parsed.getTime();
    }

    return new Date().getTime();
  }

  private createSampleTimelineData(): TimelineData[] {
    const now = new Date();
    const startTime = now.getTime();
    const endTime = startTime + (15 * 60 * 1000); // 15 minutes
    
    return [{
      category: "sample",
      start: startTime,
      end: endTime,
      settings: { fill: am5.color(0x67B7DC) },
      text: "Sample Timeline Event",
      startFormatted: new Date(startTime).toLocaleString(),
      endFormatted: new Date(endTime).toLocaleString()
    }];
  }

  private getPoints(): { x: number; y: number }[] {
    const points = [{ x: -600, y: 200 }, { x: 0, y: 200 }];

    const w = 400;
    const h = 400;
    const levelCount = 4;

    const radius = Math.min(w / (levelCount - 1) / 2, h / 2);
    const startX = radius;

    for (let i = 0; i < 25; i++) {
      const angle = 0 + i / 25 * 90;
      const centerPoint = { y: 200 - radius, x: 0 };
      points.push({ y: centerPoint.y + radius * am5.math.cos(angle), x: centerPoint.x + radius * am5.math.sin(angle) });
    }

    for (let i = 0; i < levelCount; i++) {
      if (i % 2 !== 0) {
        points.push({ y: -h / 2 + radius, x: startX + w / (levelCount - 1) * i });
        points.push({ y: h / 2 - radius, x: startX + w / (levelCount - 1) * i });

        const centerPoint = { y: h / 2 - radius, x: startX + w / (levelCount - 1) * (i + 0.5) };
        if (i < levelCount - 1) {
          for (let k = 0; k < 50; k++) {
            const angle = -90 + k / 50 * 180;
            points.push({ y: centerPoint.y + radius * am5.math.cos(angle), x: centerPoint.x + radius * am5.math.sin(angle) });
          }
        }

        if (i === levelCount - 1) {
          points.pop();
          points.push({ y: -radius, x: startX + w / (levelCount - 1) * i });
          const centerPoint = { y: -radius, x: startX + w / (levelCount - 1) * (i + 0.5) };
          for (let k = 0; k < 25; k++) {
            const angle = -90 + k / 25 * 90;
            points.push({ y: centerPoint.y + radius * am5.math.cos(angle), x: centerPoint.x + radius * am5.math.sin(angle) });
          }
          points.push({ y: 0, x: 800 });
        }
      } else {
        points.push({ y: h / 2 - radius, x: startX + w / (levelCount - 1) * i });
        points.push({ y: -h / 2 + radius, x: startX + w / (levelCount - 1) * i });
        const centerPoint = { y: -h / 2 + radius, x: startX + w / (levelCount - 1) * (i + 0.5) };
        if (i < levelCount - 1) {
          for (let k = 0; k < 50; k++) {
            const angle = -90 - k / 50 * 180;
            points.push({ y: centerPoint.y + radius * am5.math.cos(angle), x: centerPoint.x + radius * am5.math.sin(angle) });
          }
        }
      }
    }

    return points;
  }

  createChart(data: TimelineData[]): void {
    // Prevent multiple chart creations
    if (this.isCreatingChart) {
      console.log('Linear Timeline Chart - Chart creation already in progress, skipping...');
      return;
    }

    this.isCreatingChart = true;

    // Check if the chart container exists
    const chartContainer = document.getElementById('linear-timeline-chartdiv');
    if (!chartContainer) {
      console.error('Linear Timeline Chart - Chart container not found! Make sure element with id "linear-timeline-chartdiv" exists');
      this.isCreatingChart = false;
      return;
    }

    // Dispose existing chart before creating new one
    if (this.root) {
      console.log('Linear Timeline Chart - Disposing existing chart...');
      try {
        this._baseChartService.disposeChart(this.root);
      } catch (error) {
        console.warn('Error disposing chart:', error);
      }
      this.root = undefined as any;
    }

    try {
      console.log('Linear Timeline Chart - Creating new chart...');
      
      // Ensure the container is visible and has dimensions
      if (chartContainer.offsetWidth === 0 || chartContainer.offsetHeight === 0) {
        console.log('Linear Timeline Chart - Container has no dimensions, will retry...');
        this.isCreatingChart = false;
        setTimeout(() => {
          this.createChart(data);
        }, 100);
        return;
      }

      this.root = am5.Root.new("linear-timeline-chartdiv");
      this._baseChartService.attachExporting(this.root, { 
        filePrefix: 'linear-timeline',
        data: data
      });

      const wrapper = this.root.container.children.push(am5.Container.new(this.root, {
        width: am5.percent(100),
        height: am5.percent(100),
        layout: this.root.verticalLayout
      }));
      const chartHost = wrapper.children.push(am5.Container.new(this.root, {
        width: am5.percent(100),
        height: am5.percent(82)
      }));

      // Create chart
      const chart = chartHost.children.push(am5timeline.CurveChart.new(this.root, {
        width: am5.percent(100),
        height: am5.percent(100),
        wheelY: "zoomX"
      }));

      const scrollbarX = chart.set("scrollbarX", am5.Scrollbar.new(this.root, {
        orientation: "vertical",
        height: am5.percent(40),
        position: "absolute",
        centerY: am5.p50,
        x: 20
      }));

      chart.curveContainer.children.push(scrollbarX);

      const yRenderer = am5timeline.AxisRendererCurveY.new(this.root, {
        axisLocation: -0.1
      });

      yRenderer.labels.template.setAll({
        forceHidden: true
      });

      yRenderer.grid.template.set("forceHidden", true);

      // Create axes and their renderers
      const xRenderer = am5timeline.AxisRendererCurveX.new(this.root, {
        points: this.getPoints(),
        yRenderer: yRenderer,
        strokeDasharray: [2, 3],
        strokeOpacity: 0.5,
        stroke: am5.color(0x000000)
      });

      xRenderer.labels.template.setAll({
        centerY: am5.p50,
        fontSize: 11,
        minPosition: 0.01
      });

      xRenderer.labels.template.setup = (target: am5.Label) => {
        target.set("layer", 30);
        target.set("background", am5.Rectangle.new(this.root, {
          fill: am5.color(0xffffff),
          fillOpacity: 1
        }));
      };

      const yAxis = chart.yAxes.push(am5xy.CategoryAxis.new(this.root, {
        maxDeviation: 0,
        categoryField: "category",
        renderer: yRenderer
      }));

      const xAxis = chart.xAxes.push(am5xy.DateAxis.new(this.root, {
        baseInterval: { timeUnit: "minute", count: 1 },
        renderer: xRenderer,
        tooltip: am5.Tooltip.new(this.root, {})
      }));

      const series = chart.series.push(am5timeline.CurveColumnSeries.new(this.root, {
        xAxis: xAxis,
        yAxis: yAxis,
        baseAxis: yAxis,
        categoryYField: "category",
        valueXField: "end",
        openValueXField: "start",
        maskBullets: false
      }));

      // Configure series appearance
      series.columns.template.setAll({
        height: am5.percent(20),
        templateField: "settings",
        strokeOpacity: 0,
        tooltipText: "[bold]{category}[/]\n{text}\nStart: {startFormatted}\nEnd: {endFormatted}"
      });

      // Set data
      series.data.setAll(data);

      // Get unique categories for y-axis
      const categories = [...new Set(data.map(item => item.category))];
      yAxis.data.setAll(categories.map(cat => ({ category: cat })));

      const legend = wrapper.children.push(
        this._baseChartService.createLegend(this.root)
      );
      legend.setAll({
        width: am5.percent(100),
        centerX: am5.p50,
        x: am5.p50,
        marginTop: 8,
        marginBottom: 8
      });
      legend.markers.template.setAll({ width: 12, height: 12 });
      legend.labels.template.setAll({
        text: '{categoryY}',
        fontSize: 12,
        oversizedBehavior: 'truncate',
        maxWidth: 180
      });
      legend.valueLabels.template.set('forceHidden', true);
      legend.markerRectangles.template.adapters.add('fill', (fill, target) => {
        const dataItem = target.dataItem;
        if (!dataItem) {
          return fill;
        }
        const index = series.dataItems.indexOf(dataItem);
        return index >= 0 ? chart.get('colors')?.getIndex(index) : fill;
      });
      legend.markerRectangles.template.adapters.add('stroke', (stroke, target) => {
        const dataItem = target.dataItem;
        if (!dataItem) {
          return stroke;
        }
        const index = series.dataItems.indexOf(dataItem);
        return index >= 0 ? chart.get('colors')?.getIndex(index) : stroke;
      });
      const updateLegend = () => {
        if (series.dataItems?.length > 0) {
          legend.data.setAll(series.dataItems);
        }
      };
      series.events.on('datavalidated', updateLegend);
      updateLegend();
      this._baseChartService.setupLegendAnimation(legend);

      // Animate chart and series in
      series.appear(1000);
      chart.appear(1000, 100);

      console.log('Linear Timeline Chart - Chart created successfully');
      this.isCreatingChart = false;

    } catch (error) {
      console.error('Error creating linear timeline chart:', error);
      this.isCreatingChart = false;
    }
  }

  private updateChart(data: TimelineData[]): void {
    // Prevent multiple chart updates
    if (this.isCreatingChart) {
      console.log('Linear Timeline Chart - Chart update skipped, chart creation in progress...');
      return;
    }

    // Check if DOM element exists before proceeding
    const chartContainer = document.getElementById('linear-timeline-chartdiv');
    if (!chartContainer) {
      console.log('Linear Timeline Chart - DOM element not ready in updateChart, will retry later');
      // Retry after a short delay
      setTimeout(() => {
        this.updateChart(data);
      }, 100);
      return;
    }

    // Dispose existing chart first
    if (this.root) {
      console.log('Linear Timeline Chart - Disposing chart in updateChart...');
      this._baseChartService.disposeChart(this.root);
      this.root = undefined as any;
    }
    
    // Add a longer delay to ensure proper disposal and DOM element availability
    setTimeout(() => {
      this.createChart(data);
    }, 200);
    
    // Update the chart data in the registry
    BaseChartService.updateActiveChartData(data);
  }

  ngOnDestroy(): void {
    try {
      if (this.root) {
        this._baseChartService.disposeChart(this.root);
        this.root = undefined as any;
      }
    } catch (error) {
      console.warn('Error disposing chart in ngOnDestroy:', error);
    }
    
    if (this.linearTimelineGraphReportSubscription) {
      try {
        this.linearTimelineGraphReportSubscription.unsubscribe();
      } catch (error) {
        console.warn('Error unsubscribing from timeline graph report:', error);
      }
      this.linearTimelineGraphReportSubscription = null;
    }
  }
}
